/* Student "My Certificates" page — surfaces all active certificates with
 * download + verify links. Linked from the Dashboard tile grid. */
import React, { useEffect, useState } from 'react';
import { Award, Download, ShieldCheck, ExternalLink } from 'lucide-react';
import PortalTopBar from '../../components/academy/PortalTopBar';
import PortalSidebar from '../../components/academy/PortalSidebar';
import { useAuth } from '../../context/AuthContext';

export default function Certificates() {
  const { user, api } = useAuth();
  const [rows, setRows] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api.get('/api/academy/my/certificates').then(({ data }) => {
      if (active) setRows(data.rows || []);
    }).finally(() => active && setLoading(false));
    api.get('/api/academy/my/courses').then(({ data }) => {
      if (active) setCourses(data.rows || []);
    }).catch(() => {});
    return () => { active = false; };
  }, [api]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <PortalTopBar portalLabel="STUDENT ACCESS" />
      <main className="max-w-[1440px] mx-auto px-4 lg:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <PortalSidebar user={user} courses={courses} />

          <div className="flex-1 min-w-0">
            <div className="rounded-2xl overflow-hidden border border-[#E5E5E7]">
              <div className="bg-[#0e0e10] text-white px-6 py-3.5 flex items-center justify-between">
                <span className="text-[14px] font-bold tracking-[0.18em] uppercase">My Certificates</span>
                <span className="text-[12px] text-white/60">{rows.length} earned</span>
              </div>
              <div className="bg-white p-5 sm:p-7">
                {loading ? (
                  <div className="text-[13px] text-[#6E6E73]">Loading…</div>
                ) : rows.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-[#E5E5E7] p-10 text-center text-[#6E6E73]"
                    data-testid="my-certs-empty">
                    <Award className="w-10 h-10 text-[#c9a227] mx-auto mb-3" />
                    <div className="text-[14px] font-bold text-[#1D1D1F] mb-1">No certificates yet</div>
                    <p className="text-[12.5px]">
                      Finish all lessons in your enrolled programs to become eligible.
                      Once issued by the academy, your certificate will appear here.
                    </p>
                  </div>
                ) : (
                  <ul className="space-y-3">
                    {rows.map((c) => (
                      <li key={c.id}
                        data-testid={`my-cert-${c.cert_id}`}
                        className="rounded-2xl border border-[#E5E5E7] p-4 sm:p-5 flex items-start gap-4 flex-wrap">
                        <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                          style={{ background: 'linear-gradient(135deg,#fff8e7 0%,#ffe9a3 100%)' }}>
                          <Award className="w-6 h-6 text-[#a8871a]" />
                        </div>
                        <div className="flex-1 min-w-[220px]">
                          <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#a8871a]">
                            Certificate of Completion
                          </div>
                          <div className="text-[16px] font-extrabold text-[#0e0e10]">{c.program_name}</div>
                          <div className="text-[12px] text-[#6E6E73] mt-1">
                            Awarded {new Date(c.completion_date || c.issued_date).toLocaleDateString(undefined, { dateStyle: 'long' })}
                            {' · '}
                            <span className="font-mono text-[11.5px]">{c.cert_id}</span>
                          </div>
                          <div className="text-[11.5px] text-[#6E6E73] mt-1 inline-flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3 text-emerald-600" />
                            Signed by {c.signature_name} · {c.signature_title}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <a href={c.pdf_url} target="_blank" rel="noopener noreferrer"
                            data-testid={`my-cert-download-${c.cert_id}`}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10]"
                            style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}>
                            <Download className="w-3.5 h-3.5" /> Download PDF
                          </a>
                          <a href={c.verify_url} target="_blank" rel="noopener noreferrer"
                            data-testid={`my-cert-verify-${c.cert_id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider border border-[#E5E5E7] text-[#0e0e10] hover:border-[#c9a227]">
                            <ExternalLink className="w-3.5 h-3.5" /> Verify
                          </a>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
